/**
 * Couleur d'identité d'un vendeur.
 *
 * La couleur est DÉDUITE de l'identifiant du vendeur (hachage) : aucune colonne en
 * base, aucun réglage, et le même vendeur porte la même couleur sur les 3 caisses,
 * en ligne comme hors ligne, indéfiniment (l'id d'un vendeur ne change jamais).
 *
 * Un simple hachage produirait des doublons (8 vendeurs pour 10 teintes ≈ 9 chances
 * sur 10 d'avoir au moins une collision), donc l'attribution est résolue sur la liste
 * complète des vendeurs, dans l'ordre des identifiants : chacun prend sa teinte
 * préférée, sinon la suivante libre. Ordre stable ⇒ attribution stable.
 *
 * ⚠️ La couleur est un repère SECONDAIRE : le nom et la coche ✓ restent les porteurs
 * de l'information, pour rester lisible sans percevoir les nuances.
 */

export interface VendorColor {
  /** Fond plein, texte blanc lisible dessus */
  bg: string;
  /** Halo clair assorti */
  ring: string;
  /** Pastille de rappel sur les boutons inactifs */
  dot: string;
}

// Teintes foncées (700) : le blanc dessus reste lisible dans tous les cas.
// Classes écrites en toutes lettres — Tailwind ne voit pas les noms construits.
const PALETTE: VendorColor[] = [
  { bg: 'bg-emerald-700', ring: 'ring-emerald-300', dot: 'bg-emerald-600' },
  { bg: 'bg-indigo-700', ring: 'ring-indigo-300', dot: 'bg-indigo-600' },
  { bg: 'bg-rose-700', ring: 'ring-rose-300', dot: 'bg-rose-600' },
  { bg: 'bg-amber-700', ring: 'ring-amber-300', dot: 'bg-amber-600' },
  { bg: 'bg-cyan-700', ring: 'ring-cyan-300', dot: 'bg-cyan-600' },
  { bg: 'bg-violet-700', ring: 'ring-violet-300', dot: 'bg-violet-600' },
  { bg: 'bg-teal-700', ring: 'ring-teal-300', dot: 'bg-teal-600' },
  { bg: 'bg-fuchsia-700', ring: 'ring-fuchsia-300', dot: 'bg-fuchsia-600' },
  { bg: 'bg-blue-700', ring: 'ring-blue-300', dot: 'bg-blue-600' },
  { bg: 'bg-orange-700', ring: 'ring-orange-300', dot: 'bg-orange-600' },
];

/** Liste complète des identifiants vendeurs, écrite par VendorQuickBar. */
export const VENDOR_COLOR_IDS_KEY = 'pos_vendor_color_ids';

const FALLBACK: VendorColor = { bg: 'bg-gray-700', ring: 'ring-gray-300', dot: 'bg-gray-500' };

function hash(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/** Attribue une teinte à chaque vendeur, sans doublon tant qu'il reste des teintes. */
function buildAssignment(vendorIds: string[]): Record<string, number> {
  const taken = new Set<number>();
  const assignment: Record<string, number> = {};

  // Ordre des identifiants : indépendant de l'ordre d'affichage, donc stable.
  for (const id of [...vendorIds].sort()) {
    let index = hash(id) % PALETTE.length;
    for (let step = 0; step < PALETTE.length && taken.has(index); step++) {
      index = (index + 1) % PALETTE.length;
    }
    taken.add(index);
    assignment[id] = index;
  }
  return assignment;
}

let cachedKey = '';
let cachedAssignment: Record<string, number> = {};

/**
 * Couleur d'un vendeur. La liste complète sert à éviter les doublons ; si elle n'est
 * pas fournie, on retombe sur le cache vendeurs du POS (présent aussi hors ligne).
 */
export function getVendorColor(vendorId: string | null | undefined, vendorIds?: string[]): VendorColor {
  if (!vendorId) return FALLBACK;

  let ids = vendorIds;
  if (!ids) {
    try {
      // Liste COMPLÈTE (vendeurs désactivés inclus) : désactiver ou réactiver
      // quelqu'un ne doit pas redistribuer les couleurs des autres.
      const stored = localStorage.getItem(VENDOR_COLOR_IDS_KEY);
      if (stored) {
        ids = JSON.parse(stored);
      } else {
        const cached = localStorage.getItem('pos_vendors_cache');
        ids = cached ? JSON.parse(cached).map((v: { id: string }) => v.id) : [vendorId];
      }
    } catch {
      ids = [vendorId];
    }
  }
  if (!ids?.includes(vendorId)) ids = [...(ids ?? []), vendorId];

  const key = [...ids].sort().join('|');
  if (key !== cachedKey) {
    cachedKey = key;
    cachedAssignment = buildAssignment(ids);
  }

  return PALETTE[cachedAssignment[vendorId]] ?? FALLBACK;
}

/** Mémorise la liste complète des vendeurs pour que l'attribution soit la même partout. */
export function rememberVendorIds(vendorIds: string[]): void {
  try {
    localStorage.setItem(VENDOR_COLOR_IDS_KEY, JSON.stringify([...vendorIds].sort()));
  } catch {
    // localStorage plein — la couleur retombera sur le cache vendeurs
  }
}
